import * as THREE from "three";
import { facet, merge } from "@rpg/meshkit";
import { clampAttachment, clampCard, type Attachment, type Clamped, type FigureCard, type JointSpec, type Vec3 } from "./card";
import { chainPoints, createChain, linksGeometry, restMatrix, type Chain } from "./chain";
import { buildPiece, pieceRng, shapeGeometry, surface, type Built, type BuildContext } from "./pieces";

// Build a figure from its card: the joint tree as Object3Ds, every piece built in model space and moved into its
// joint's space, and all of a joint's pieces MERGED into one mesh (decisions 0029, 0034: one draw call per joint, not
// per part); each spring chain is one mesh too (chain.ts). Every mesh shares the one material the game passes in (the
// look's surface material), so a whole cast compiles one program. Attachments (weapons, props) merge into the socket
// joint's mesh and swap by rebuilding that one mesh.

/** What `buildFigure` needs from the game. */
export interface BuildOptions {
  /** The one material every mesh uses (vertex colours, a `glow` attribute; e.g. @rpg/look's low-poly surface). */
  material: THREE.Material;
  /** Turn a card colour (hex or palette key) into a colour. */
  colour(c: string): THREE.Color;
  /** Overrides the card's seed (a crowd member's variation). */
  seed?: number;
  /** One draw call for the whole figure (a crowd member): every joint's pieces merged into one skinned mesh, each vertex
   *  following its joint rigidly (one bone per joint, weight 1), instead of a mesh per joint. The joints and `meshes`
   *  stay (each joint's mesh kept as an unrendered bone the motion scales), so the motion and the pose layer work
   *  unchanged; call `syncSkin` after posing so hidden joints (arms not acting) hide their vertices. Spring chains stay
   *  their own meshes. */
  single?: boolean;
}

/** A built figure. */
export interface Figure {
  /** The figure's root: the game places it (position, yaw); its feet are at y = 0. Tagged `userData.character`. */
  root: THREE.Group;
  /** The joint tree's parent under the root: identity at rest; the pose layer (pose.ts) turns and offsets the whole
   *  figure here (a dodge roll, a stagger's step back) without touching the root the game places. */
  pivot: THREE.Group;
  /** Every joint's Object3D by id (chain links are not joints here: their chain's mesh poses them). */
  joints: Map<string, THREE.Object3D>;
  /** Each joint's rest position in model space. */
  rest: Map<string, THREE.Vector3>;
  /** The merged mesh on each joint that has pieces. */
  meshes: Map<string, THREE.Mesh>;
  chains: Map<string, Chain>;
  /** The card as built (clamped). */
  card: FigureCard;
  /** What clamping moved (empty for a card inside its ranges). */
  clamped: Clamped[];
  /** The card's uniform scale (1 if none): positions, pieces, chains and grips are built at it. */
  scale: number;
  /** The attachment on the socket now, if any (its grip at the figure's scale). */
  attachment: Attachment | null;
  /** Put an attachment on the socket (null: none). Rebuilds the socket joint's one mesh. */
  attach(a: Attachment | null): void;
  /** Draw calls (visible meshes, shadows not counted), triangles and vertices. */
  stats(): { draws: number; triangles: number; vertices: number; meshes: number };
  /** The one skinned mesh of a `single` figure (null otherwise). */
  skin: THREE.SkinnedMesh | null;
  /** A `single` figure: collapse the vertices of joints hidden this frame (call after posing; a no-op otherwise). */
  syncSkin(): void;
  dispose(): void;
}

/** Build a figure from a card (clamped to its ranges first). */
export function buildFigure(input: FigureCard, o: BuildOptions): Figure {
  const { card, clamped } = clampCard(input);
  const seed = o.seed ?? card.seed ?? 1;
  const k = card.scale ?? 1;
  const root = new THREE.Group();
  root.name = "figure";
  root.userData.character = true;
  const pivot = new THREE.Group();
  pivot.name = "pivot";
  root.add(pivot);

  // joints: Object3Ds at their rest positions relative to their parents
  const specs = new Map<string, JointSpec>(card.joints.map((j) => [j.id, j]));
  const joints = new Map<string, THREE.Object3D>();
  const rest = new Map<string, THREE.Vector3>();
  const make = (id: string, seen: Set<string> = new Set()): THREE.Object3D => {
    const have = joints.get(id);
    if (have) return have;
    const s = specs.get(id);
    if (!s) throw new Error(`@rpg/figure: unknown joint "${id}"`);
    if (seen.has(id)) throw new Error(`@rpg/figure: joint cycle at "${id}"`);
    seen.add(id);
    const parent = s.parent ? make(s.parent, seen) : pivot;
    const obj = new THREE.Object3D();
    obj.name = id;
    const at = new THREE.Vector3(...s.at).multiplyScalar(k);
    const pAt = s.parent ? rest.get(s.parent)! : new THREE.Vector3();
    obj.position.copy(at).sub(pAt);
    parent.add(obj);
    joints.set(id, obj);
    rest.set(id, at);
    return obj;
  };
  for (const j of card.joints) make(j.id);

  // pieces, by joint (chain links collected per chain)
  const byJoint = new Map<string, THREE.BufferGeometry[]>();
  const byLink = new Map<string, { link: number; geo: THREE.BufferGeometry }[]>();
  const chainSpecs = new Map((card.chains ?? []).map((c) => [c.id, c]));
  const linkOf = (joint: string): { chain: string; link: number } | null => {
    const m = /^(.*)\.(\d+)$/.exec(joint);
    if (!m || !chainSpecs.has(m[1]!)) return null;
    return { chain: m[1]!, link: Number(m[2]) };
  };
  const put = (b: Built): void => {
    if (k !== 1) b.geo.scale(k, k, k);
    const l = linkOf(b.joint);
    if (l) {
      const spec = chainSpecs.get(l.chain)!;
      if (l.link >= chainPoints(spec).length - 1) throw new Error(`@rpg/figure: chain "${l.chain}" has no link ${l.link}`);
      const list = byLink.get(l.chain) ?? [];
      list.push({ link: l.link, geo: b.geo });
      byLink.set(l.chain, list);
      return;
    }
    if (!joints.has(b.joint)) throw new Error(`@rpg/figure: a piece names unknown joint "${b.joint}"`);
    const list = byJoint.get(b.joint) ?? [];
    list.push(b.geo);
    byJoint.set(b.joint, list);
  };
  card.pieces.forEach((p, i) => {
    const ctx: BuildContext = { colour: o.colour, rng: pieceRng(seed, i, p.seed ?? 0) };
    if (p.kind === "links") {
      const spec = chainSpecs.get(p.chain);
      if (!spec) throw new Error(`@rpg/figure: links piece names unknown chain "${p.chain}"`);
      for (const l of linksGeometry(p, spec, ctx)) put({ joint: `${p.chain}.${l.link}`, geo: l.geo });
      return;
    }
    for (const b of buildPiece(p, ctx)) put(b);
  });

  // one mesh per joint: every piece moved into the joint's space and merged
  const meshes = new Map<string, THREE.Mesh>();
  const baseGeos = new Map<string, THREE.BufferGeometry[]>();
  const toJoint = (id: string, g: THREE.BufferGeometry): THREE.BufferGeometry => {
    const at = rest.get(id)!;
    return g.translate(-at.x, -at.y, -at.z);
  };
  const setMesh = (id: string, geos: THREE.BufferGeometry[]): void => {
    const old = meshes.get(id);
    if (old) {
      old.geometry.dispose();
      old.removeFromParent();
      meshes.delete(id);
    }
    if (!geos.length) return;
    const m = new THREE.Mesh(merge(geos.map((g) => g.clone())), o.material);
    m.name = `${id}:mesh`;
    m.castShadow = true;
    m.receiveShadow = true;
    // a single figure draws its skin: the joint's mesh is the bone its vertices follow, never drawn itself
    if (o.single) {
      m.visible = false;
      m.userData.bone = true;
    }
    joints.get(id)!.add(m);
    meshes.set(id, m);
  };
  for (const [id, geos] of byJoint) {
    const local = geos.map((g) => toJoint(id, g));
    baseGeos.set(id, local);
    setMesh(id, local);
  }

  // chains, hanging from their joints
  const chains = new Map<string, Chain>();
  for (const spec of card.chains ?? []) {
    const anchor = joints.get(spec.joint);
    if (!anchor) throw new Error(`@rpg/figure: chain "${spec.id}" hangs from unknown joint "${spec.joint}"`);
    const geos = byLink.get(spec.id) ?? [];
    if (!geos.length) continue;
    const scaled = { ...spec, points: spec.points.map((p) => p.map((v) => v * k) as Vec3) };
    chains.set(spec.id, createChain(scaled, geos, o.material, root, anchor, restMatrix(rest.get(spec.joint)!.toArray() as Vec3)));
  }

  // a single figure: every joint mesh's geometry merged, in model space at rest, into one skinned mesh whose bones are
  // the joint meshes (rigid skinning: one bone a vertex, weight 1)
  let skin: THREE.SkinnedMesh | null = null;
  let boneList: THREE.Mesh[] = [];
  const rebuildSkin = (): void => {
    if (!o.single) return;
    if (skin) {
      skin.geometry.dispose();
      skin.removeFromParent();
      skin.skeleton.dispose();
    }
    const ids = [...meshes.keys()];
    boneList = ids.map((id) => meshes.get(id)!);
    // the rest pose, whatever the figure's pose now: each joint mesh's vertices back in model space at its rest place
    const parts = ids.map((id, i) => {
      const at = rest.get(id)!;
      const g = meshes.get(id)!.geometry.clone().translate(at.x, at.y, at.z);
      const n = g.getAttribute("position").count;
      const idx = new Uint16Array(n * 4);
      const w = new Float32Array(n * 4);
      for (let v = 0; v < n; v++) {
        idx[v * 4] = i;
        w[v * 4] = 1;
      }
      g.setAttribute("skinIndex", new THREE.Uint16BufferAttribute(idx, 4));
      g.setAttribute("skinWeight", new THREE.Float32BufferAttribute(w, 4));
      return g;
    });
    if (!parts.length) {
      skin = null;
      return;
    }
    const geo = merge(parts);
    for (const g of parts) g.dispose();
    skin = new THREE.SkinnedMesh(geo, o.material);
    skin.name = "skin";
    skin.castShadow = true;
    skin.receiveShadow = true;
    root.add(skin);
    skin.updateMatrixWorld(true);
    // each bone's inverse at rest: (the skin's world × the joint's rest place)⁻¹
    const inverses = ids.map((id) => {
      const at = rest.get(id)!;
      return skin!.matrixWorld.clone().multiply(new THREE.Matrix4().makeTranslation(at.x, at.y, at.z)).invert();
    });
    skin.bind(new THREE.Skeleton(boneList as unknown as THREE.Bone[], inverses), skin.matrixWorld);
    // culled by its rest pose's bounds, grown for a reach or a stride
    geo.computeBoundingSphere();
    skin.boundingSphere = geo.boundingSphere!.clone();
    skin.boundingSphere.radius += 0.4 * k;
  };
  const hiddenUp = (m: THREE.Object3D): boolean => {
    for (let q: THREE.Object3D | null = m.parent; q && q !== root; q = q.parent) if (!q.visible) return true;
    return false;
  };

  let attachment: Attachment | null = null;
  const fig: Figure = {
    root,
    pivot,
    joints,
    rest,
    meshes,
    chains,
    card,
    clamped,
    scale: k,
    get attachment() {
      return attachment;
    },
    attach(a) {
      const socket = card.socket;
      if (!socket || !joints.has(socket)) throw new Error("@rpg/figure: this card has no socket");
      attachment = a ? clampAttachment(a).attachment : null;
      if (attachment && k !== 1) attachment = { ...attachment, grip: { ...attachment.grip, at: attachment.grip.at.map((v) => v * k) as Vec3 } };
      const geos = [...(baseGeos.get(socket) ?? [])];
      if (attachment)
        attachment.pieces.forEach((p, i) => {
          const ctx: BuildContext = { colour: o.colour, rng: pieceRng(seed + 17, i, p.seed ?? 0) };
          // an attachment's pieces are built in its own space, which is the socket's (the motion turns the socket to the grip)
          const built = p.kind === "shape" ? [facet(shapeGeometry(p), surface(p, ctx))] : buildPiece({ ...p, joint: socket }, ctx).map((b) => b.geo);
          for (const g of built) geos.push(k !== 1 ? g.scale(k, k, k) : g);
        });
      setMesh(socket, geos);
      rebuildSkin();
    },
    get skin() {
      return skin;
    },
    syncSkin() {
      if (!skin) return;
      for (const b of boneList) {
        if (hiddenUp(b)) b.scale.set(1e-4, 1e-4, 1e-4);
        else if (b.scale.x < 1e-3) {
          b.scale.x = 1;
          b.scale.z = 1;
          if (b.scale.y < 1e-3) b.scale.y = 1;
        }
      }
    },
    stats() {
      let draws = 0;
      let triangles = 0;
      let vertices = 0;
      let count = 0;
      root.traverse((x) => {
        const m = x as THREE.Mesh;
        if (!m.isMesh || m.userData.bone) return;
        count++;
        let vis = true;
        for (let q: THREE.Object3D | null = m; q && q !== root.parent; q = q.parent) if (!q.visible) vis = false;
        const n = m.geometry.getAttribute("position").count;
        vertices += n;
        if (!vis) return;
        draws++;
        triangles += (m.geometry.index?.count ?? n) / 3;
      });
      return { draws, triangles, vertices, meshes: count };
    },
    dispose() {
      root.traverse((x) => (x as THREE.Mesh).geometry?.dispose());
      skin?.skeleton.dispose();
      root.removeFromParent();
    },
  };
  rebuildSkin();
  return fig;
}
